// src/pages/ItemPage.jsx

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import BidModal from "../components/BidModal";
import LoadingSpinner from "../components/LoadingSpinner";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { fetchItems, submitBid } from "../api";
import { imgurSize } from "../utils/imgur";
import "../App.css";

function ItemPage() {
  const { itemId } = useParams();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  const [bid, setBid] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const items = await fetchItems();
        if (cancelled) return;
        const found = items.find((i) => i.id === itemId);
        if (!found) {
          setError("We couldn't find that artwork. It may have been removed.");
        } else {
          setItem(found);
          setBid(found.reservation_price);
        }
      } catch (err) {
        console.error("❌ Failed to fetch item:", err);
        if (!cancelled) {
          setError(
            err.status === 0
              ? "Could not reach the gallery. Check your connection and try again."
              : "This artwork couldn't be loaded just now. Please refresh in a moment."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const formatZAR = (amount) =>
    new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const handleSubmit = async () => {
    // Guard against a second tap while the first request is in flight — on a
    // slow connection nothing visibly changes, so double-tapping is natural
    // and would place two bids.
    if (submitting) return;

    setMessage("");
    setSubmitting(true);

    try {
      if (!executeRecaptcha) {
        setMessage("Verification isn't ready yet — please try again in a moment.");
        return;
      }

      let token;
      try {
        token = await executeRecaptcha("submit_bid");
      } catch (err) {
        console.error("❌ reCAPTCHA failed:", err);
        setMessage("We couldn't verify your browser. Please refresh and try again.");
        return;
      }
      if (!token) {
        setMessage("Verification failed. Please refresh and try again.");
        return;
      }

      const data = await submitBid(item.id, {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        amount: bid,
        recaptcha_token: token,
      });

      if (data.status === "success") {
        setSucceeded(true);

        /* The backend now returns `email_sent`. Previously this said "a
           confirmation is on its way" unconditionally — and for several days
           that was false, because the Gmail app password was silently
           returning 535. A bidder had no proof their bid existed and no reason
           to follow up.

           `!== false` rather than a truthiness check, so an older backend that
           omits the field still shows the optimistic message rather than
           warning about an email that probably did send. */
        const sent = data.email_sent !== false;
        setEmailSent(sent);

        if (sent) {
          setMessage(
            `Thank you ${firstName} — your bid of ${formatZAR(bid)} has been placed. ` +
            `A confirmation is on its way to ${email}.`
          );
        } else {
          setMessage(
            `Thank you ${firstName} — your bid of ${formatZAR(bid)} on "${item.title}" has been recorded. ` +
            `We couldn't send a confirmation email just now, so please make a note of it. ` +
            `Speak to the gallery if you'd like it confirmed.`
          );
        }
      } else {
        setMessage(data.detail || "Your bid couldn't be placed. Please try again.");
      }
    } catch (err) {
      // ApiError from api.js carries the backend's own message, so a rejected
      // bid explains itself ("Bid below reservation price") rather than
      // showing a generic network error.
      console.error("❌ Error placing bid:", err);
      setMessage(
        err.status === 0
          ? "Could not reach the auction service. Check your connection and try again."
          : err.message || "Your bid couldn't be placed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="gallery-card">
        <p className="error-text" role="alert">{error}</p>
        <Link to="/" className="cta-button">Back to the gallery</Link>
      </div>
    );
  }

  return (
    <div className="gallery-card">
      {item.images.map((url, i) => (
        <div key={i} className="item-image-wrapper">
          {/* imgurSize(…, "h") requests the 1024px variant. The originals are
              3-5MB each, and this is the LCP element on the page a QR code
              lands on — often over gallery wifi, with the visitor standing in
              front of the painting.

              No width/height attributes: the artworks are 60x60 and 60x90, so
              any fixed ratio letterboxes some of them. .item-image uses
              height:auto and lets each size to its own proportions.

              First image eager and high priority; the rest lazy-load. */}
          <img
            src={imgurSize(url, "h")}
            alt={
              i === 0
                ? `${item.title} by ${item.artist}`
                : `${item.title}, view ${i + 1}`
            }
            className="item-image"
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : undefined}
            decoding="async"
          />
        </div>
      ))}

      <p className="item-artist-metadata">
        <em>{item.artist}</em> ({item.year})
      </p>
      <h1 className="item-title">{item.title}</h1>
      <p className="short-desc text-block">{item.short_description}</p>

      <div className="metadata text-block">
        <p><strong>Medium:</strong> {item.medium}</p>
        <p><strong>Size:</strong> {item.size}</p>
        <p><strong>Techniques:</strong> {item.techniques}</p>
      </div>

      <details className="text-block">
        <summary className="details-summary">View full description</summary>
        <p className="full-description">{item.full_description}</p>
      </details>

      {/* Exhibition-only pieces are shown but can't be bid on. Enforced
          server-side too (main.py) — hiding a button is presentation, not a
          control. */}
      {item.for_sale ? (
        <>
          <p className="reservation-line">
            Bidding opens at <strong>{formatZAR(item.reservation_price)}</strong>
          </p>

          {/* Sticky on mobile so the CTA stays reachable however far the
              visitor scrolls through the description — they arrive by QR code
              with the artwork in front of them, and this is the only action. */}
          <div className="bid-cta-bar">
            <button onClick={() => setIsModalOpen(true)} className="cta-button">
              Place a Bid
            </button>
          </div>
        </>
      ) : (
        <p className="not-for-sale-note">
          This piece is part of the exhibition and is not available for purchase.
        </p>
      )}

      {succeeded && !isModalOpen && (
        <p
          className={`confirmation-message${emailSent ? "" : " confirmation-warning"}`}
          role="status"
        >
          {message}
        </p>
      )}

      {isModalOpen && item.for_sale && (
        <BidModal
          item={item}
          bid={bid}
          setBid={setBid}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          email={email}
          setEmail={setEmail}
          phone={phone}
          setPhone={setPhone}
          onSubmit={handleSubmit}
          submitting={submitting}
          onClose={() => {
            setIsModalOpen(false);
            if (!succeeded) setMessage("");
          }}
          message={message}
          messageTone={emailSent ? "success" : "warning"}
        />
      )}
    </div>
  );
}

export default ItemPage;